import { useCallback, useEffect, useRef, useState } from 'react';
import { decodeJwt } from 'jose';
import { clearSession, loadSession, saveSession, updateSessionToken, } from '@/lib/session-storage';
const ONE_MINUTE_IN_MS = 60 * 1000;
// Get the API base URL - use the embed script's src origin when available,
// otherwise window.location.origin. We try both the chat-popup attribute and
// the voice-embed attribute so either bundle works when embedded cross-origin
// (or inside a sandboxed iframe where window.location.origin is "null").
function getApiBaseUrl() {
    if (typeof window !== 'undefined') {
        const scriptTag = document.querySelector('script[data-lk-sandbox-id], script[data-lk-web-widget-id]');
        if (scriptTag === null || scriptTag === void 0 ? void 0 : scriptTag.src) {
            try {
                return new URL(scriptTag.src).origin;
            }
            catch (_a) {
                // Fall back to window.location.origin
            }
        }
    }
    return typeof window !== 'undefined' ? window.location.origin : '';
}
export default function useConnectionDetails(appConfig) {
    const [connectionDetails, setConnectionDetails] = useState(null);
    const [restoredMessages, setRestoredMessages] = useState([]);
    const initialLoadDone = useRef(false);
    // Capture session identity at mount time so it survives async races
    // (e.g. sessionStorage expiry or clearSession before reconnect completes)
    const savedSessionRef = useRef(null);
    const fetchConnectionDetails = useCallback(async (opts) => {
        var _a, _b, _c;
        setConnectionDetails(null);
        const url = new URL((_a = process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT) !== null && _a !== void 0 ? _a : '/api/connection-details', getApiBaseUrl());
        const res = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Sandbox-Id': (_b = appConfig.sandboxId) !== null && _b !== void 0 ? _b : '',
            },
            body: JSON.stringify({
                web_widget_id: appConfig.webWidgetId,
                room_config: appConfig.agentName
                    ? { agents: [{ agent_name: appConfig.agentName }] }
                    : undefined,
                // Pass existing session identity for reconnection
                room_name: opts === null || opts === void 0 ? void 0 : opts.roomName,
                participant_identity: opts === null || opts === void 0 ? void 0 : opts.participantIdentity,
                // Pass conversation context for agent metadata
                conversation_id: opts === null || opts === void 0 ? void 0 : opts.conversationId,
                visitor_id: opts === null || opts === void 0 ? void 0 : opts.visitorId,
                visitor_email: opts === null || opts === void 0 ? void 0 : opts.visitorEmail,
                visitor_phone: opts === null || opts === void 0 ? void 0 : opts.visitorPhone,
                visitor_name: opts === null || opts === void 0 ? void 0 : opts.visitorName,
                is_followup: (_c = opts === null || opts === void 0 ? void 0 : opts.isFollowup) !== null && _c !== void 0 ? _c : false,
                // Interaction source: 'web_chat' (default) or 'web_call' (voice demo)
                source: appConfig.source,
            }),
        });
        if (!res.ok) {
            throw new Error(`Error fetching connection details: ${res.status}`);
        }
        const data = await res.json();
        // Extract the actual participant identity from the JWT token.
        // The token is the source of truth — its `sub` claim contains the
        // identity that LiveKit will use in the room.  Storing anything else
        // causes an identity mismatch on reconnect, which makes the agent
        // ignore messages from the "new" participant.
        let tokenIdentity;
        try {
            const jwt = decodeJwt(data.participantToken);
            tokenIdentity = jwt.sub;
        }
        catch (_d) {
            // Fallback handled below
        }
        // Persist to sessionStorage
        const existingSession = loadSession(appConfig.webWidgetId);
        if (existingSession && (opts === null || opts === void 0 ? void 0 : opts.roomName)) {
            // Reconnecting to existing session — update token, keep messages
            updateSessionToken(data.participantToken, data.serverUrl, appConfig.webWidgetId);
        }
        else {
            // Brand new session
            const newSession = {
                roomName: data.roomName,
                participantIdentity: tokenIdentity ||
                    (opts === null || opts === void 0 ? void 0 : opts.participantIdentity) ||
                    `chat_user_${Math.floor(Math.random() * 10000)}`,
                serverUrl: data.serverUrl,
                participantToken: data.participantToken,
                createdAt: Date.now(),
                lastActivity: Date.now(),
                messages: [],
            };
            saveSession(newSession, appConfig.webWidgetId);
        }
        setConnectionDetails(data);
        return data;
    }, [appConfig.sandboxId, appConfig.webWidgetId, appConfig.agentName]);
    // On mount: restore messages from sessionStorage for immediate display.
    // Do NOT fetch a token here — the connect effect in agent-client handles
    // that when the popup opens, avoiding wasted API calls if the user never
    // opens the chat and preventing double-fetches on page reload.
    useEffect(() => {
        if (initialLoadDone.current)
            return;
        initialLoadDone.current = true;
        const existingSession = loadSession(appConfig.webWidgetId);
        if (existingSession) {
            // Snapshot identity so it survives sessionStorage expiry or
            // clearSession that may happen during later async reconnection.
            savedSessionRef.current = {
                roomName: existingSession.roomName,
                participantIdentity: existingSession.participantIdentity,
            };
            // Restore chat history for immediate display
            setRestoredMessages(existingSession.messages);
        }
    }, [appConfig.webWidgetId]);
    const isConnectionDetailsExpired = useCallback(() => {
        const token = connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken;
        if (!token)
            return true;
        const jwtPayload = decodeJwt(token);
        if (!jwtPayload.exp)
            return true;
        // jwtPayload.exp is in seconds (per JWT spec), convert to ms
        const expiresAtMs = jwtPayload.exp * 1000;
        return Date.now() > expiresAtMs - ONE_MINUTE_IN_MS;
    }, [connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken]);
    const existingOrRefreshConnectionDetails = useCallback(async () => {
        if (isConnectionDetailsExpired() || !connectionDetails) {
            // Prefer live sessionStorage, fall back to mount-time snapshot
            const session = loadSession(appConfig.webWidgetId);
            const sessionInfo = session
                ? { roomName: session.roomName, participantIdentity: session.participantIdentity }
                : savedSessionRef.current;
            return fetchConnectionDetails(sessionInfo !== null && sessionInfo !== void 0 ? sessionInfo : undefined);
        }
        return connectionDetails;
    }, [
        connectionDetails,
        fetchConnectionDetails,
        isConnectionDetailsExpired,
        appConfig.webWidgetId,
    ]);
    const clearCurrentSession = useCallback(() => {
        clearSession(appConfig.webWidgetId);
        setRestoredMessages([]);
    }, [appConfig.webWidgetId]);
    return {
        connectionDetails,
        restoredMessages,
        refreshConnectionDetails: fetchConnectionDetails,
        existingOrRefreshConnectionDetails,
        clearCurrentSession,
    };
}
